import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import classNames from 'classnames'

import type { TabType } from '@/router/tabsSystems/types'
import Flex from '@/elements/Flex'

import './Tabs.scss'

interface TabsProps<T extends object = Record<string, never>> {
  tabs: TabType<T>[]
  extraInfo?: Record<string, string>
  children?: React.ReactNode
  grow?: boolean
  keepQueryParamsButExclude?: string[]
}

//
// How to work with "keepQueryParamsButExclude":
// keepQueryParamsButExclude = undefined  -> don't keep any query params -> default behavior
// keepQueryParamsButExclude = []         -> keep all query params
// keepQueryParamsButExclude = ['page']   -> keep all query params except "page"
//

function Tabs<T extends object = Record<string, never>>({
  tabs,
  extraInfo,
  children,
  grow = false,
  keepQueryParamsButExclude = undefined,
}: TabsProps<T>) {
  const currentTab = window.location.pathname.split('/').pop()
  if (!currentTab) return null

  const tabData = tabs.find(({ url }) => url === currentTab)
  if (!tabData) return null

  const TabContent = tabData.component

  return (
    <Flex wfull grow className='tabs-wrapper'>
      <Flex className='left-fade' />
      <Flex
        wfull
        inline
        gap={1}
        pt={1}
        pb={3}
        mb={1}
        className='tabs-header'
        scroll='horizontal'
      >
        {tabs.map(tab => (
          <Tab
            key={tab.url}
            tab={tab}
            active={tab.url === tabData.url}
            extraContent={extraInfo?.[tab.url]}
            keepQueryParamsButExclude={keepQueryParamsButExclude}
            id={`tab-${tab.url}`}
          />
        ))}
      </Flex>

      <Flex wfull hfull={grow}>
        {TabContent ? (
          <TabContent {...(tabData.props ?? ({} as T))} />
        ) : (
          children
        )}
      </Flex>
      <Flex className='right-fade' />
    </Flex>
  )
}

function Tab<T extends object>({
  tab,
  active,
  extraContent,
  keepQueryParamsButExclude = undefined,
  id,
}: {
  tab: TabType<T>
  active: boolean
  extraContent?: string
  keepQueryParamsButExclude?: string[]
  id: string
}) {
  const navigate = useNavigate()
  const tabRef = useRef<HTMLButtonElement>(null)

  const navigateToTab = useCallback(
    (tabLink: string) => {
      let nextUrl = window.location.pathname.replace(/\/[^/]+$/, `/${tabLink}`)

      // Don't keep any query params
      if (keepQueryParamsButExclude === undefined || !Array.isArray(keepQueryParamsButExclude)) {
        navigate(nextUrl)
        return
      }

      // Keep all query params
      if (keepQueryParamsButExclude.length === 0) {
        const currentParams = window.location.search
        if (currentParams) nextUrl = `${nextUrl}${currentParams}`
        navigate(nextUrl)
        return
      }

      // Preserve existing query params except excluded ones
      const nextUrlParams = new URLSearchParams()
      Array.from(window.location.search.slice(1).split('&')).forEach(param => {
        const [key, value] = param.split('=')
        if (key && value && !keepQueryParamsButExclude?.includes(key)) {
          nextUrlParams.append(key, value)
        }
      })
      if (nextUrlParams.toString()) nextUrl = `${nextUrl}?${nextUrlParams.toString()}`
      navigate(nextUrl)
    },
    [navigate, keepQueryParamsButExclude]
  )

  useEffect(() => {
    if (active && tabRef.current) {
      tabRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
  }, [active])

  return (
    <button
      ref={tabRef}
      id={id}
      onClick={() => navigateToTab(tab.url)}
      className={classNames('tab', { active })}
      disabled={tab.disabled}
    >
      {tab.label}
      {extraContent && <span>{extraContent}</span>}
    </button>
  )
}

export default Tabs
